/**
 * PreSet AGM with UFS — 문서 준비 payload 빌드.
 *
 * 7개 문서 중 조건에 따라 포함/제외:
 *  - Consent to Short Notice → isShortNotice=true 일 때만 포함
 *  - Minutes of AGM (Multiple) → memberType='multiple' 일 때만
 *  - Minutes of AGM (Sole Corporate) → memberType='sole_corporate' 일 때만
 *  - Minutes of AGM (Sole Individual) → memberType='sole_individual' 일 때만
 *  - Attendance List, Directors' Resolutions, Notice of AGM → 항상 포함
 */
import { classifyAgmUfsDoc } from './agmUfsDocVariableMap';
import {
  buildAgmUfsAttendanceSignerVariables,
  buildAgmUfsConsentSignerVariables,
  buildAgmUfsMinutesMultipleChairmanSignerVariables,
  buildAgmUfsMinutesSoleCorporateSignerVariables,
  buildAgmUfsMinutesSoleIndividualSignerVariables,
} from './agmUfsAttendanceSigners';
import { toAgmUfsDirectorsResolutionOnlyVariables, type AgmUfsFormModel } from './useFormPayload';
import { buildSgPresetPrepareDocumentItem } from '../../../presetDocCustSave';

export interface SelectionDocRow {
  tmpId?: number | null;
  templateName?: string;
}

const CONSENT_SHORT_NOTICE = /consent\s*to\s*short\s*notice/i;
const MINUTES_MULTIPLE = /minutes.*multiple/i;
const MINUTES_SOLE_CORPORATE = /minutes.*sole\s*corporate/i;
const MINUTES_SOLE_INDIVIDUAL = /minutes.*sole\s*individual/i;

type MemberType = 'multiple' | 'sole_corporate' | 'sole_individual';

function shouldIncludeDoc(
  templateName: string,
  isShortNotice: boolean,
  memberType: MemberType,
): boolean {
  if (CONSENT_SHORT_NOTICE.test(templateName)) return isShortNotice;
  if (MINUTES_MULTIPLE.test(templateName)) return memberType === 'multiple';
  if (MINUTES_SOLE_CORPORATE.test(templateName)) return memberType === 'sole_corporate';
  if (MINUTES_SOLE_INDIVIDUAL.test(templateName)) return memberType === 'sole_individual';
  return true;
}

export function buildAgmUfsPresetPrepareDocuments(input: {
  selectionDocuments: SelectionDocRow[];
  commonFormData: Record<string, any>;
  formModel: AgmUfsFormModel;
  personName: string;
  entityCd?: string;
  isShortNotice: boolean;
  memberType: MemberType;
}) {
  const drOnlyVars = toAgmUfsDirectorsResolutionOnlyVariables(input.formModel);

  return (input.selectionDocuments || [])
    .filter((d) => d.tmpId)
    .filter((d) =>
      shouldIncludeDoc(d.templateName || '', input.isShortNotice, input.memberType),
    )
    .map((d) => {
      const templateName = d.templateName || '';
      const docKind = classifyAgmUfsDoc(templateName);
      let commonFormData = { ...input.commonFormData };

      if (docKind === 'directors_resolution') {
        commonFormData = { ...commonFormData, ...drOnlyVars };
      } else if (docKind === 'attendance') {
        const attendanceSigners = buildAgmUfsAttendanceSignerVariables(input.formModel);
        if (attendanceSigners) {
          commonFormData = { ...commonFormData, ...attendanceSigners };
        }
      } else if (docKind === 'consent') {
        const consentSigners = buildAgmUfsConsentSignerVariables(input.formModel);
        if (consentSigners) {
          commonFormData = { ...commonFormData, ...consentSigners };
        }
      } else if (docKind === 'minutes_multiple') {
        const minutesSigners = buildAgmUfsMinutesMultipleChairmanSignerVariables(input.formModel);
        if (minutesSigners) {
          commonFormData = { ...commonFormData, ...minutesSigners };
        }
      } else if (docKind === 'minutes_sole_corporate') {
        const minutesSigners = buildAgmUfsMinutesSoleCorporateSignerVariables(input.formModel);
        if (minutesSigners) {
          commonFormData = { ...commonFormData, ...minutesSigners };
        }
      } else if (docKind === 'minutes_sole_individual') {
        const minutesSigners = buildAgmUfsMinutesSoleIndividualSignerVariables(input.formModel);
        if (minutesSigners) {
          commonFormData = { ...commonFormData, ...minutesSigners };
        }
      }

      return buildSgPresetPrepareDocumentItem({
        templateId: d.tmpId as number | string,
        templateName,
        personName: input.personName,
        commonFormData,
        entityCd: input.entityCd,
      });
    });
}
